import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { productSearchVariantFetch, productVariantFetch, setSubStage } from '../../../../slices/eligibility/eligibilitySlice'
import { useDispatch, useSelector } from "react-redux";
import NoImageComponent from '../../../common/NoImage';
import { toTitleCaseAdvanced } from '../../../../utils/functions';
import ImageComponent from '../../../common/Image';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import Loader from '../../../common/Loader';

// Extract variant item component to reduce duplication
const VariantItem = ({ model, onSelect, className = "" }) => {
    const getVariantImageInfo = useCallback((categoryName) => {
        const name = categoryName?.toLowerCase();
        const imageMap = {
            'mobile phone': { src: 'mobilephn.png', isSpecificBrand: true },
            'air cooler': { src: 'Ac.svg', isSpecificBrand: true },
            'deep freezer': { src: 'fridge.svg', isSpecificBrand: true },
            'air conditioner': { src: 'Ac.svg', isSpecificBrand: true },
            'refrigerator': { src: 'fridge.svg', isSpecificBrand: true },
            'washing machine': { src: 'washingmech.svg', isSpecificBrand: true },
            'led tv': { src: 'television.svg', isSpecificBrand: true },
        };
        return imageMap[name] || { src: 'no2.svg', isSpecificBrand: false };
    }, []);

    const variantImageInfo = useMemo(() =>
        getVariantImageInfo(model?.category), [model?.category, getVariantImageInfo]
    );

    const handleSelect = useCallback(() => {
        onSelect(model?.brand_name, variantImageInfo, model);
    }, [model, variantImageInfo, onSelect]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelect();
        }
    }, [handleSelect]);

    return (
        <div
            className={`product-details px-3 py-2 rounded ${className}`}
            role="button"
            tabIndex={0}
            onClick={handleSelect}
            onKeyDown={handleKeyDown}
        >
            <div className="d-flex align-items-center">
                {model?.category_image_url ? (
                    <img loading="lazy"
                        src={model.category_image_url}
                        className="img-fluid"
                        alt={model.name || "Variant"}
                    />
                ) : variantImageInfo.isSpecificBrand ? (
                    <ImageComponent
                        fileName={variantImageInfo.src}
                        className="img-fluid"
                        alt={model?.name || 'Variant'}
                        width="110px"
                    />
                ) : (
                    <NoImageComponent
                        fileName="no2.svg"
                        className="img-fluid"
                        alt={model?.name || 'Variant'}
                    />
                )}
                <div className="border-end fridge-tab mx-2"></div>
                <p className="fw-normal variant-item">{model?.description}</p>
            </div>
        </div>
    );
};

VariantItem.propTypes = {
    model: PropTypes.shape({
        model: PropTypes.string,
        category: PropTypes.string,
        brand_name: PropTypes.string,
        description: PropTypes.string,  // Add this line to validate description
    }).isRequired,
    onSelect: PropTypes.func.isRequired,
    className: PropTypes.string,
};

// Extract search modal component
const SearchModal = ({ isOpen, onClose, filteredModels, onModelSelect, searchTerm, onSearchChange }) => {
    if (!isOpen) return null;

    return (
        <>
            <div
                className="modal show fade"
                id="searchrole"
                style={{ display: "block" }}
                aria-hidden="true"
                aria-labelledby="Dealerrole"
                tabIndex="-1"
                onClick={onClose}
            >
                <div className="modal-dialog bottom-modal">
                    <div className="modal-content blue" onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                            }
                        }}>
                        <div className="modal-header">
                            <div className="top-draw"></div>
                        </div>
                        <div className="d-flex align-items-center justify-content-between px-3">
                            <h6 className="mb-1">Variant Search</h6>
                        </div>
                        <div className="modal-body searchrole p-4" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <form
                                className="col-12 col-lg-auto mb-3 mb-lg-0 me-lg-3 position-relative"
                                role="search"
                                onSubmit={(e) => e.preventDefault()}
                            >
                                <input
                                    type="search"
                                    className="form-control input-underline"
                                    placeholder="Search by Variant"
                                    aria-label="Search"
                                    value={searchTerm}
                                    onChange={onSearchChange}
                                />
                                <div className="has-i-search hidden-search-box">
                                    <span className="icon-searchnormal fs-4 inside-search border-0"></span>
                                </div>
                            </form>

                            {filteredModels?.length > 0 ? (
                                <div className="row mb-3 mt-3">
                                    <div className="col-md-12 RightPane">
                                        <p className="text-secondary">Search Results</p>
                                    </div>
                                    {filteredModels.map((item) => (
                                        <div key={item?.description} className="col-md-12 mb-3">
                                            <VariantItem
                                                model={item}
                                                onSelect={(brandName, imageInfo, modelData) => {
                                                    onModelSelect(brandName, imageInfo, modelData);
                                                    onClose();
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                searchTerm.trim().length > 0 && (
                                    <div className="col-md-12 mt-2">
                                        <p className="text-muted">No matching Variant</p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

// Extract navigation breadcrumb component
const NavigationBreadcrumb = ({ brand, product, dispatch }) => {
    const handleNavigation = useCallback((stage) => {
        dispatch(setSubStage(stage));
    }, [dispatch]);

    const handleKeyDown = useCallback((stage) => (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleNavigation(stage);
        }
    }, [handleNavigation]);

    return (
        <div className="d-flex gap-2 align-items-center">
            <p className="Pname-i mb-0"></p>
            <div
                className="lh-0"
                role="button"
                tabIndex={0}
                onClick={() => handleNavigation("brand")}
                onKeyDown={handleKeyDown("brand")}
            >
                <p className="text-decoration-none grey small mb-0">Brand</p>
                <p class="small mb-0 fw-600">{toTitleCaseAdvanced(brand || '')}</p>
            </div>
            <span className="icon-arrowright-L1 blue"></span>
            <div
                className="lh-0"
                role="button"
                tabIndex={0}
                onClick={() => handleNavigation("category")}
                onKeyDown={handleKeyDown("category")}
            >
                <p className="text-decoration-none grey small mb-0">Category</p>
                <p class="small mb-0 fw-600">{toTitleCaseAdvanced(product || '')}</p>
            </div>
            <span className="icon-arrowright-L1 blue"></span>
            <div className="lh-0">
                <p className="text-decoration-none blue small mb-0 fs-14">Select Model</p>
            </div>
        </div>
    );
};

function Variant(props) {
    const { subStage, stage } = useSelector(state => state.eligibility)
    const dispatch = useDispatch();
    const [searchPopup, setSearchPopup] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true); // Track if more data is available

    const [loader, setLoader] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (searchTerm.trim().length >= 2) {
                searchVariant(searchTerm.trim());
            } else {
                setSearchResults([]);
            }
        }, 1000); // debounce input

        return () => clearTimeout(delayDebounce);
    }, [searchTerm]);

    const searchVariant = async (term) => {
        if (!referenceId || !dealerCode) return;

        setIsSearching(true);
        let dealer_Code = dealerCode?.split('-')[0]?.trim();

        try {
            setLoader(true)
            const response = await dispatch(productSearchVariantFetch({
                reference_id: referenceId,
                dealer_code: dealer_Code,
                type: "brand_sku",
                page: 1,
                limit: 20,
                description: term, // 👈 pass search term
                ...props?.data?.product
            })).unwrap();
            setLoader(false)

            if (response?.response_code === "S100") {
                setSearchResults(response?.response_data || []);
            } else {
                !response?.payload?.error &&
                toast.error(response?.response_string || "Failed to fetch search results");
            }

        } catch (error) {
            toast.error("Search failed");
        } finally {
            setIsSearching(false);
        }
    };

    const urlParams = new URLSearchParams(window.location.search);
    const referenceId = urlParams.get('referenceId');
    const { dealerCode } = useSelector(state => state.dashboard)

    const productData = useSelector(state => state?.eligibility?.variantData)

    const observerRef = useRef(null);

    useEffect(() => {
        observerRef.current = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && hasMore && !loader) {
                setPage(prev => prev + 1);
            }
        });

        const sentinel = document.querySelector("#scroll-sentinel");
        if (sentinel) observerRef.current.observe(sentinel);

        return () => {
            if (observerRef.current && sentinel) {
                observerRef.current.unobserve(sentinel);
            }
        };
    }, [hasMore, loader]);


    useEffect(() => {
        if (stage === 'product' && subStage === 'variant') {
            fetchProductData(page);
        }
    }, [stage, subStage, page]);

    const fetchProductData = useCallback(async (currentPage) => {
        if (!referenceId || !dealerCode) return;
        if (!hasMore) return; // ✅ stop if no more pages

        let dealer_Code = dealerCode?.split('-')[0]?.trim();
        setLoader(true);

        try {
            const response = await dispatch(productVariantFetch({
                reference_id: referenceId,
                dealer_code: dealer_Code,
                type: "brand_sku",
                page: currentPage,
                limit: 10,
                ...props?.data?.product
            })).unwrap();

            setLoader(false);

            if (response?.response_code !== 'S100') {
                !response?.payload?.error &&
                toast.error(response?.response_string);
                setHasMore(false); // ✅ stop further fetches
                return;
            }

            const items = response?.response_data ?? response?.data ?? [];

            if (!items || items.length < 10) {
                setHasMore(false); // ✅ if less than limit → stop scroll
            }
        } catch (error) {
            setLoader(false);
            toast.error('An error occurred while fetching product data');
            setHasMore(false); // ✅ fail-safe stop
        }
    }, [dispatch, dealerCode, referenceId, props?.data?.product, hasMore]);

    useEffect(() => {
        const handleScroll = () => {
            if (
                window.innerHeight + window.scrollY >= document.body.offsetHeight - 100 &&
                !loader &&
                hasMore
            ) {
                setPage(prev => prev + 1);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [loader, hasMore]);

    const filteredModels = useMemo(() => {
        if (!Array.isArray(productData)) return []; // ✅ safe fallback

        return searchTerm
            ? productData.filter(item =>
                item?.description?.toLowerCase().includes(searchTerm.toLowerCase())
            )
            : productData;
    }, [productData, searchTerm]);

    const setModel = useCallback((description, pic, data) => {
        props?.setData(prev => ({
            ...prev,
            model: description,
            image: pic?.src,
            jsonData: data
        }));
        dispatch(setSubStage("summary"));
    }, [props, dispatch]);

    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
    }, []);

    const handleSearchToggle = useCallback(() => {
        setSearchPopup(prev => !prev);
    }, []);

    const handleSearchKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSearchPopup(true);
        }
    }, []);

    return (
        <>
            {loader === true &&
                <>
                    <Loader />
                </>}
            <div className="col-md-9 col-sm-12 Side-bank-Right variant-div">
                <div className="col-12 mb-1 static-tab">
                    <div className="d-flex align-items-center justify-content-between">
                        <NavigationBreadcrumb
                            brand={props?.data?.brand?.brand_name}
                            product={props?.data?.product?.category}
                            dispatch={dispatch}
                        />
                        <div className="fs-3">
                            <span
                                className="icon-searchnormal inside-search border p-1 rounded text-secondary border-secondary fs-6"
                                role="button"
                                tabIndex={0}
                                onKeyDown={handleSearchKeyDown}
                                onClick={handleSearchToggle}
                            />
                        </div>
                    </div>
                </div>
                <div className="row gy-4 mt-2">
                    {/* Only show models or 'No Model found' after loader is false */}
                    {loader ? null : (
                        Array.isArray(productData) && productData.length > 0 ? (
                            productData.map((model, index) => (
                                <div className="col-12 col-lg-12 col-md-12" key={index}>
                                    <VariantItem model={model} onSelect={setModel} />
                                </div>
                            ))
                        ) : (
                            <h6 className="text-center">No Model found</h6>
                        )
                    )}
                    <div id="scroll-sentinel" style={{ height: 1 }}></div>
                </div>

                <SearchModal
                    isOpen={searchPopup}
                    onClose={() => setSearchPopup(false)}
                    filteredModels={searchResults}
                    onModelSelect={setModel}
                    searchTerm={searchTerm}
                    onSearchChange={handleSearchChange}
                />
            </div>
        </>
    );
}

export default Variant;